import { useState, useRef, useEffect } from 'react'

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']
const DOW = ['Su','Mo','Tu','We','Th','Fr','Sa']

function toISO(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return y + '-' + m + '-' + day
}

function fmtDisplay(iso) {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return iso
  return m[3] + '/' + m[2] + '/' + m[1]
}

export default function DateField({ value, onChange, placeholder = 'DD/MM/YYYY', style = {} }) {
  const [open, setOpen] = useState(false)
  const [flipUp, setFlipUp] = useState(false)
  const [flipLeft, setFlipLeft] = useState(false)
  const [view, setView] = useState(() => {
    const base = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value + 'T00:00:00') : new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })
  const ref = useRef(null)

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  useEffect(() => {
    if (open && value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const d = new Date(value + 'T00:00:00')
      setView(new Date(d.getFullYear(), d.getMonth(), 1))
    }
  }, [open]) // eslint-disable-line

  const selISO = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''
  const todayISO = toISO(new Date())

  const year = view.getFullYear()
  const month = view.getMonth()
  const firstDow = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells = []
  for (let i = 0; i < firstDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  function pick(d) { onChange(toISO(new Date(year, month, d))); setOpen(false) }
  function shift(delta) { setView(new Date(year, month + delta, 1)) }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        value={fmtDisplay(value)}
        placeholder={placeholder}
        onFocus={(e) => { const r = e.target.getBoundingClientRect(); setFlipUp(r.bottom > window.innerHeight * 0.55); setFlipLeft(r.left + 248 > window.innerWidth - 12); setOpen(true) }}
        onChange={() => {}}
        readOnly
        style={{ cursor: 'pointer', ...style }}
      />
      {open && (
        <div style={{ ...cal.pop, ...(flipUp ? { top: 'auto', bottom: '100%', marginTop: 0, marginBottom: 4 } : {}), ...(flipLeft ? { left: 'auto', right: 0 } : {}) }}>
          <div style={cal.head}>
            <button type="button" onClick={() => shift(-1)} style={cal.nav}>&#8249;</button>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select value={month} onChange={e => setView(new Date(year, Number(e.target.value), 1))} style={cal.sel}>
                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select value={year} onChange={e => setView(new Date(Number(e.target.value), month, 1))} style={cal.sel}>
                {Array.from({ length: (new Date().getFullYear() + 10) - 1940 + 1 }, (_, k) => 1940 + k).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </span>
            <button type="button" onClick={() => shift(1)} style={cal.nav}>&#8250;</button>
          </div>
          <div style={cal.grid}>
            {DOW.map(d => <span key={d} style={cal.dow}>{d}</span>)}
            {cells.map((d, i) => {
              if (d === null) return <span key={'e' + i} />
              const iso = toISO(new Date(year, month, d))
              const isSel = iso === selISO
              const isToday = iso === todayISO
              return (
                <button key={d} type="button" onClick={() => pick(d)}
                  style={{ ...cal.day, ...(isToday && !isSel ? cal.today : {}), ...(isSel ? cal.sel : {}) }}
                >{d}</button>
              )
            })}
          </div>
          <div style={cal.foot}>
            <button type="button" onClick={() => { onChange(todayISO); setOpen(false) }} style={cal.todayBtn}>Today</button>
            {value && <button type="button" onClick={() => { onChange(''); setOpen(false) }} style={cal.clearBtn}>Clear</button>}
          </div>
        </div>
      )}
    </div>
  )
}

const cal = {
  pop: { position: 'absolute', zIndex: 9999, top: '100%', left: 0, marginTop: 4, background: 'var(--ink2, #0f1822)', border: '0.5px solid var(--border2, #2a3a4a)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.55)', padding: 10, width: 248 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  title: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  sel: { background: 'var(--hull, #0f1822)', border: '0.5px solid var(--border2, #2a3a4a)', color: 'var(--text)', fontSize: 12, padding: '3px 6px', borderRadius: 6, fontFamily: 'inherit', cursor: 'pointer' },
  nav: { background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 8px', borderRadius: 6 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 },
  dow: { textAlign: 'center', fontSize: 10, color: 'var(--muted)', padding: '4px 0', fontWeight: 600 },
  day: { background: 'transparent', border: 'none', color: 'var(--text)', cursor: 'pointer', fontSize: 12.5, padding: '6px 0', borderRadius: 6, fontFamily: 'inherit' },
  today: { outline: '1px solid var(--gold2, #8a5c0a)', outlineOffset: -1 },
  sel: { background: 'var(--gold2, #8a5c0a)', color: '#fff', fontWeight: 700 },
  foot: { display: 'flex', gap: 8, marginTop: 8, paddingTop: 8, borderTop: '0.5px solid var(--border)' },
  todayBtn: { background: 'transparent', border: 'none', color: 'var(--gold2, #8a5c0a)', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' },
  clearBtn: { background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', marginLeft: 'auto' },
}
